import {
	addDays,
	differenceInCalendarDays,
	differenceInDays,
	differenceInHours,
	differenceInMinutes,
	differenceInMonths,
	endOfDay,
	format,
	getMinutes,
	isAfter,
	isSameDay,
	isThisYear,
	isToday,
	isTomorrow,
	isValid,
	isYesterday,
	parseISO,
	startOfDay,
	subDays,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";

import { plural } from "./strings";

/**
 * Input type for calendarDateFormat - accepts Date, string (ISO), or number (timestamp).
 * Bad values are here for moment compatibility
 */
export type DateInput = Date | string | number | null | undefined;

/**
 * Specification for calendar date formatting - maps relative time periods to format strings.
 * Format strings use date-fns syntax (e.g., "MMM d" not "MMM D").
 *
 * Can be either a format string or a function that receives the date and returns a format string.
 */
export interface CalendarSpec {
	/** Format for dates that are today */
	sameDay?: string | ((date: Date) => string);
	/** Format for dates that are tomorrow */
	nextDay?: string | ((date: Date) => string);
	/** Format for dates that are yesterday */
	lastDay?: string | ((date: Date) => string);
	/** Format for dates within the next 7 days (excluding today/tomorrow) */
	nextWeek?: string | ((date: Date) => string);
	/** Format for dates within the last 7 days (excluding today/yesterday) */
	lastWeek?: string | ((date: Date) => string);
	/** Format for all other dates */
	sameElse?: string | ((date: Date) => string);
}

const DEFAULT_CALENDAR_SPEC: Required<CalendarSpec> = {
	sameDay: "'Today at' p",
	nextDay: "'Tomorrow at' p",
	lastDay: "'Yesterday at' p",
	nextWeek: "EEEE 'at' p",
	lastWeek: "'Last' EEEE 'at' p",
	sameElse: "MMM d, yyyy",
};

/**
 * Format a date relative to today, similar to moment's .calendar() method.
 * Uses date-fns format strings.
 *
 * @param input - Date to format (Date object, ISO string, or timestamp)
 * @param spec - Optional calendar specification for custom formats
 * @returns Formatted date string
 *
 * @example
 * // With defaults
 * calendarDateFormat(new Date()) // "Today at 3:00 PM"
 *
 * @example
 * // With custom spec
 * calendarDateFormat(date, {
 *   sameDay: "h:mma",
 *   lastDay: "'Yesterday'",
 *   sameElse: "MMM do yyyy"
 * })
 */
export function calendarDateFormat(input: DateInput, spec?: CalendarSpec): string {
	const date = toDate(input);
	if (!isValid(date)) {
		return "Invalid date";
	}
	const now = new Date();
	const mergedSpec = { ...DEFAULT_CALENDAR_SPEC, ...spec };

	const resolveFormat = (formatOrFn: string | ((date: Date) => string)): string => {
		return typeof formatOrFn === "function" ? formatOrFn(date) : formatOrFn;
	};

	if (isToday(date)) {
		return format(date, resolveFormat(mergedSpec.sameDay));
	}

	if (isTomorrow(date)) {
		return format(date, resolveFormat(mergedSpec.nextDay));
	}

	if (isYesterday(date)) {
		return format(date, resolveFormat(mergedSpec.lastDay));
	}

	const daysDiff = differenceInDays(startOfDay(date), startOfDay(now));

	// Within next week (2-6 days from now)
	if (daysDiff > 1 && daysDiff < 7) {
		return format(date, resolveFormat(mergedSpec.nextWeek));
	}

	// Within last week (-6 to -2 days from now)
	if (daysDiff < -1 && daysDiff > -7) {
		return format(date, resolveFormat(mergedSpec.lastWeek));
	}

	return format(date, resolveFormat(mergedSpec.sameElse));
}

/**
 * Convert DateInput to a Date object
 */
function toDate(input: DateInput): Date {
	if (input === undefined) {
		return new Date();
	}
	if (input === null) {
		return new Date(Number.NaN);
	}
	if (input instanceof Date) {
		return input;
	}
	if (typeof input === "string") {
		const iso = parseISO(input);
		if (!isNaN(iso.getTime())) {
			return iso;
		}
		return new Date(input);
	}
	return new Date(input);
}

/**
 * Parse a date input with fallback behavior matching moment.js.
 * First tries parseISO for strings (strict ISO 8601), then falls back to native Date parsing.
 *
 * Behavior parity notes:
 * - `undefined` -> now (same as `moment(undefined)` / `moment()`)
 * - `Date` -> cloned Date
 * - `null` -> invalid (returns null)
 *
 * Why parseISO first for strings?
 * - For date-only strings (YYYY-MM-DD), parseISO treats them as local time (matching moment)
 * - new Date() treats date-only strings as UTC, causing timezone offset issues
 *
 * @param input - Date-like input to parse
 * @returns Date object or null if invalid
 *
 * @example
 * parseDate("2024-01-15T10:30:00Z")     // UTC datetime ✅
 * parseDate("2024-01-15")               // Local midnight ✅ (parseISO)
 * parseDate("01/15/2024")               // US format ✅ (Date fallback)
 * parseDate(new Date())                 // Date clone ✅
 * parseDate(undefined)                  // current date/time ✅
 * parseDate("invalid")                  // null
 */
export function parseDate(input: DateInput): Date | null {
	if (input === undefined) {
		return new Date();
	}

	if (input === null) {
		return null;
	}

	if (input instanceof Date) {
		return isNaN(input.getTime()) ? null : new Date(input.getTime());
	}

	if (typeof input === "string") {
		const iso = parseISO(input);
		if (!isNaN(iso.getTime())) return iso;
	}

	// Fall back to native Date parsing like moment does
	const js = new Date(input);
	return isNaN(js.getTime()) ? null : js;
}

export function formatDatetime(datetime: unknown): string {
	const parsed = toValidDate(datetime);
	if (!parsed) {
		return "Invalid date";
	}
	return format(parsed, "MMMM d, yyyy h:mm a");
}

/**
 * Converts a ms amount to a difference in humanized time.
 * eg "less than one day", "3 days", "4 years"
 * @param expiresIn
 */
export function millisecondsToHumanizedDate(
	expiresIn: number,
	accuracy: "years" | "months" | "days" | "hours" | "minutes" = "days",
) {
	let remainingSeconds = expiresIn / 1000;

	const SECONDS_IN_A_MINUTE = 60;
	const SECONDS_IN_A_HOUR = SECONDS_IN_A_MINUTE * 60;
	const SECONDS_IN_A_DAY = SECONDS_IN_A_HOUR * 24;
	const SECONDS_IN_A_MONTH = SECONDS_IN_A_DAY * 30;
	const SECONDS_IN_A_YEAR = SECONDS_IN_A_DAY * 365;

	const years = Math.floor(remainingSeconds / SECONDS_IN_A_YEAR);
	remainingSeconds -= SECONDS_IN_A_YEAR * years;
	const months = Math.floor(remainingSeconds / SECONDS_IN_A_MONTH);
	remainingSeconds -= SECONDS_IN_A_MONTH * years;
	const days = Math.floor(remainingSeconds / SECONDS_IN_A_DAY);
	remainingSeconds -= SECONDS_IN_A_DAY * days;
	const hours = Math.floor(remainingSeconds / SECONDS_IN_A_HOUR);
	remainingSeconds -= SECONDS_IN_A_HOUR * hours;
	const minutes = Math.floor(remainingSeconds / SECONDS_IN_A_MINUTE);

	if (years) return `${years} year${plural(years)}`;
	if (accuracy === "years") return "less than one year";
	if (months) return `${months} month${plural(months)}`;
	if (accuracy === "months") return "less than one month";
	if (days) return `${days} day${plural(days)}`;
	if (accuracy === "days") return "less than one day";
	if (hours) return `${hours} hour${plural(hours)}`;
	if (accuracy === "hours") return "less than one hour";
	if (minutes) return `${minutes} minute${plural(minutes)}`;
	return "less than one minute";
}

export function humanizeDateAgo(date: string) {
	const now = new Date();
	const givenDate = parseDate(date);
	if (!givenDate) {
		return "Invalid date";
	}
	const minutes = differenceInMinutes(now, givenDate);

	if (minutes === 0) {
		return "Just now";
	} else if (differenceInHours(now, givenDate) < 1) {
		return `${minutes}m ago`;
	} else if (differenceInDays(now, givenDate) < 1) {
		const hours = differenceInHours(now, givenDate);
		return `${hours}h ago`;
	} else if (isSameDay(givenDate, subDays(now, 1))) {
		return "Yesterday";
	} else {
		return format(givenDate, "MMM d, yyyy");
	}
}

export function getDateStringInTimezone(date: Date, timezone: string, includeWeekday = false) {
	const zonedDate = toZonedTime(date, timezone);
	const zonedToday = toZonedTime(new Date(), timezone);
	const dayDifference = differenceInCalendarDays(startOfDay(zonedDate), startOfDay(zonedToday));

	if (dayDifference === 0) {
		return "Today";
	} else if (dayDifference === 1) {
		return "Tomorrow";
	} else if (dayDifference === -1) {
		return "Yesterday";
	}

	return getAbsoluteDateString(zonedDate, includeWeekday);
}

function formatAllDayDateRange(start: string | Date, end: string | Date, timezone?: string | null) {
	const startDate = new Date(start);
	const endDate = new Date(end);
	const zonedStart = timezone ? toZonedTime(startDate, timezone) : startDate;
	const zonedEnd = timezone ? toZonedTime(endDate, timezone) : endDate;

	const startDay = startOfDay(zonedStart);
	const endDay = startOfDay(zonedEnd);
	const endDayInclusive = differenceInCalendarDays(endDay, startDay) > 0 ? addDays(endDay, -1) : endDay;

	if (differenceInCalendarDays(endDayInclusive, startDay) === 0) {
		return timezone ? getDateStringInTimezone(startDay, timezone) : getDateString(startDay);
	}

	return `${getAbsoluteDateString(startDay)} - ${getAbsoluteDateString(endDayInclusive)}`;
}

export function meetingDateFormat(
	start: string | Date,
	end: string | Date,
	isAllDay: boolean,
	timezone?: string | null,
) {
	if (isAllDay) {
		return formatAllDayDateRange(start, end, timezone);
	}

	const startDateObj = new Date(start);
	const endDateObj = new Date(end);

	if (!toValidDate(startDateObj) || !toValidDate(endDateObj)) {
		return "Invalid date";
	}

	if (isAfter(endDateObj, endOfDay(startDateObj))) {
		if (startDateObj.getHours() === 0 && endDateObj.getHours() === 0) {
			const startDate = format(startDateObj, "MMM d");
			const endDate = format(isAllDay ? addDays(endDateObj, -1) : endDateObj, "MMM d");
			if (startDate === endDate) {
				return startDate;
			} else {
				return `${startDate} - ${endDate}`;
			}
		} else {
			const startDate = format(startDateObj, "MMM d 'at' h:mm a");
			const endDate = format(endDateObj, "MMM d 'at' h:mm a");
			return `${startDate} - ${endDate}`;
		}
	} else {
		let eventDate;
		const now = new Date();

		if (isSameDay(startDateObj, now)) {
			eventDate = "Today";
		} else if (isSameDay(startDateObj, subDays(now, 1))) {
			eventDate = "Yesterday";
		} else if (isSameDay(startDateObj, addDays(now, 1))) {
			eventDate = "Tomorrow";
		} else if (differenceInMonths(startDateObj, now) >= 6) {
			eventDate = format(startDateObj, "MMM d, yyyy");
		} else {
			eventDate = format(startDateObj, "MMM d");
		}

		const startTime = format(startDateObj, "h:mm a");
		const endTime = format(endDateObj, "h:mm a");
		return `${eventDate} at ${startTime} - ${endTime}`;
	}
}

function toValidDate(input: unknown): Date | null {
	if (input == null) {
		return null;
	}

	if (input instanceof Date) {
		return isNaN(input.getTime()) ? null : input;
	}

	if (typeof input === "string") {
		return parseDate(input);
	}

	if (typeof input === "number") {
		const date = new Date(input);
		return isNaN(date.getTime()) ? null : date;
	}

	const date = new Date(input as string | number | Date);
	return isNaN(date.getTime()) ? null : date;
}

/** Turn a Date into a human-readable string. If you need the string to use words like "Today" or "Tomorrow",
 * see `getDateString()`.
 */
export function getAbsoluteDateString(date: Date, includeWeekday = false) {
	return date.toLocaleDateString("en", {
		month: "short",
		day: "numeric",
		year: isThisYear(date) ? undefined : "numeric",
		weekday: includeWeekday ? "short" : undefined,
	});
}

/** Turn a Date into a human-readble string, making use of words like "Tomorrow" and "Yesterday" */
export function getDateString(date: Date, includeWeekday = false) {
	if (isToday(date)) {
		return "Today";
	} else if (isTomorrow(date)) {
		return "Tomorrow";
	} else if (isYesterday(date)) {
		return "Yesterday";
	}
	return getAbsoluteDateString(date, includeWeekday);
}

/** Get the time from a Date, excluding the minutes if it's on the hour. i.e. "2 PM" or "5:15 PM" */
export function getTimeString(date: Date) {
	return date.toLocaleTimeString("en", {
		hour: "numeric",
		minute: getMinutes(date) === 0 ? undefined : "2-digit",
	});
}

type DurationUnit = "d" | "h" | "m" | "s" | "ms";

export interface FormatDurationOptions {
	/** Unit style: 'compact' = "1h", 'short' = "1 hr", 'long' = "1 hour" */
	style?: "compact" | "short" | "long";
	/** Largest unit to display (default: 'h') */
	granularity?: DurationUnit;
	/** Smallest unit to display, or 'adaptive' for top 2 significant units (default: 's') */
	precision?: DurationUnit | "adaptive";
	/** Decimal places for the smallest unit. 0 = round + auto-fallback if result is 0 (default: 0) */
	decimals?: number;
}

const UNIT_ORDER: DurationUnit[] = ["d", "h", "m", "s", "ms"];
const UNIT_SECONDS: Record<DurationUnit, number> = {
	d: 86400,
	h: 3600,
	m: 60,
	s: 1,
	ms: 0.001,
};
const UNIT_LABELS = {
	compact: { d: "d", h: "h", m: "m", s: "s", ms: "ms" },
	short: { d: " day", h: " hr", m: " min", s: " sec", ms: " ms" },
	long: { d: " day", h: " hour", m: " minute", s: " second", ms: " millisecond" },
} as const;

export function formatDuration(durationSeconds: number, options?: FormatDurationOptions): string {
	const style = options?.style ?? "compact";
	const granularity = options?.granularity ?? "h";
	const precision = options?.precision ?? "s";
	const decimals = options?.decimals ?? 0;
	const labels = UNIT_LABELS[style];
	const separator = " ";

	const granularityIdx = UNIT_ORDER.indexOf(granularity);
	const precisionIdx = precision === "adaptive" ? -1 : UNIT_ORDER.indexOf(precision);

	const pluralize = (value: number, unit: DurationUnit) => {
		const label = labels[unit];
		if (style !== "compact" && value !== 1) return `${label}s`;
		return label;
	};

	// Get units to consider (from granularity down to precision or all for adaptive)
	const unitsToConsider =
		precision === "adaptive" ? UNIT_ORDER.slice(granularityIdx) : UNIT_ORDER.slice(granularityIdx, precisionIdx + 1);

	// For adaptive: find top 2 significant units
	if (precision === "adaptive") {
		let remaining = durationSeconds;
		const significantUnits: { unit: DurationUnit; value: number }[] = [];

		for (const unit of unitsToConsider) {
			const unitValue = UNIT_SECONDS[unit];
			const value = Math.floor(remaining / unitValue);
			if (value > 0 || significantUnits.length > 0) {
				significantUnits.push({ unit, value });
				remaining = remaining % unitValue;
				if (significantUnits.length === 2) break;
			}
		}

		// If nothing significant, show 0 of the smallest considered unit
		if (significantUnits.length === 0) {
			const smallestUnit = unitsToConsider[unitsToConsider.length - 1];
			return `0${pluralize(0, smallestUnit)}`;
		}

		// Filter out trailing zeros for cleaner output
		const filtered = significantUnits.filter((u, i) => i === 0 || u.value > 0);
		return filtered.map(({ unit, value }) => `${value}${pluralize(value, unit)}`).join(separator);
	}

	// Fixed precision: calculate each unit value
	let remaining = durationSeconds;
	const parts: { unit: DurationUnit; value: number }[] = [];

	for (let i = 0; i < unitsToConsider.length; i++) {
		const unit = unitsToConsider[i];
		const unitValue = UNIT_SECONDS[unit];
		const isLast = i === unitsToConsider.length - 1;

		if (isLast) {
			// Last unit: apply decimals/rounding
			const rawValue = remaining / unitValue;
			const value = decimals > 0 ? Number(rawValue.toFixed(decimals)) : Math.round(rawValue);
			parts.push({ unit, value });
		} else {
			const value = Math.floor(remaining / unitValue);
			parts.push({ unit, value });
			remaining = remaining % unitValue;
		}
	}

	// Auto-fallback: if decimals=0 and result is all zeros, fall back to next smaller unit
	if (decimals === 0 && parts.every(p => p.value === 0)) {
		const nextUnitIdx = precisionIdx + 1;
		if (nextUnitIdx < UNIT_ORDER.length) {
			const fallbackUnit = UNIT_ORDER[nextUnitIdx];
			const fallbackValue = Math.round(durationSeconds / UNIT_SECONDS[fallbackUnit]);
			return `${fallbackValue}${pluralize(fallbackValue, fallbackUnit)}`;
		}
	}

	// Format output - skip leading zeros, but keep trailing/middle zeros
	const firstNonZero = parts.findIndex(p => p.value > 0);
	const relevantParts = firstNonZero === -1 ? [parts[parts.length - 1]] : parts.slice(firstNonZero);

	return relevantParts.map(({ unit, value }) => `${value}${pluralize(value, unit)}`).join(separator);
}

/** Human-readable duration: "1 hr 23 min" with adaptive precision */
export function formatHumanReadableDuration(durationSeconds: number): string {
	// Round to whole seconds so adaptive formatting never emits millisecond units.
	return formatDuration(Math.round(durationSeconds), { style: "short", precision: "adaptive" });
}

/** @deprecated Use formatDuration with { style: 'compact', precision: 'm' } */
export function getDurationString(durationInSeconds: number): string {
	return formatDuration(durationInSeconds, { style: "compact", precision: "m" });
}

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = MS_PER_SECOND * 60;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;

/**
 * Convert milliseconds to hours (decimal).
 * Replaces: moment.duration(ms).asHours()
 */
export function durationInHours(ms: number): number {
	return ms / MS_PER_HOUR;
}

/**
 * Convert milliseconds to days (decimal).
 * Replaces: moment.duration(ms).asDays()
 */
export function durationInDays(ms: number): number {
	return ms / MS_PER_DAY;
}

/**
 * Get the hours component of a duration (0-23).
 * Replaces: moment.duration(ms).hours()
 */
export function durationHoursComponent(ms: number): number {
	return Math.floor(ms / MS_PER_HOUR) % 24;
}

/**
 * Convert milliseconds to minutes (decimal).
 * Replaces: moment.duration(ms).asMinutes()
 */
export function durationInMinutes(ms: number): number {
	return ms / MS_PER_MINUTE;
}

/**
 * Convert milliseconds to seconds (decimal).
 * Replaces: moment.duration(ms).asSeconds()
 */
export function durationInSeconds(ms: number): number {
	return ms / MS_PER_SECOND;
}
